import type { ReactNode } from "react";
import type { PageKey, AppNavigationTarget } from "../../appNavTypes";
import { GameNav } from "./GameNav";
import { GamePlayerBar } from "./GamePlayerBar";
import { PLAYER_BAR_PAGES } from "./playerBarPages";

/* Which screens carry the player bar — the lobbies and the tills — is `playerBarPages.ts`,
   with the reasoning and the exclusions on the list itself. */

interface GameShellProps {
  activePage: PageKey;
  onNavigate: (target: AppNavigationTarget) => void;
  /** Authenticated identity / guest sentinel address — drives the global attention alert. */
  actingAddress: string | null;
  /** Login / account control from App, rendered in the rail (this is the only nav — the web
   *  top nav has been removed, so the rail must carry login/account). */
  accountSlot?: ReactNode;
  children: ReactNode;
}

/**
 * Persistent game shell — the app's single, global navigation. It wraps every route with the
 * primary navigation (a left side-rail on desktop, a bottom tab bar on mobile) plus a HUD
 * "needs you" alert. The web top nav has been removed; this rail replaces it everywhere.
 *
 * On the game lobbies listed in PLAYER_BAR_PAGES it also pins the compact GamePlayerBar
 * (trainer picture, name, Suika balance + "+") above the stage.
 *
 * The global attention alert keys on the acting identity (`actingAddress`) and hides itself
 * when nobody is signed in — never gates on a Sui wallet (CLAUDE.md identity-vs-custody).
 */
export function GameShell({
  activePage,
  onNavigate,
  actingAddress,
  accountSlot,
  children,
}: GameShellProps) {
  return (
    <div className="game-shell">
      <div className="game-shell__stage">
        {PLAYER_BAR_PAGES.includes(activePage) && <GamePlayerBar />}
        {children}
      </div>

      <GameNav
        activePage={activePage}
        onNavigate={onNavigate}
        actingAddress={actingAddress}
        accountSlot={accountSlot}
      />
    </div>
  );
}
